import React from 'react';
import { buildCustomerPriceBreakdown } from '../lib/customerPriceBreakdown';
import { formatMoney } from '../lib/safeData';

function formatLocal(amount, currency) {
  if (amount == null || Number.isNaN(Number(amount))) return '—';
  if (currency === 'USD') return `$${Number(amount).toFixed(2)}`;
  return `${currency} ${Number(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * Guaranteed Premium Delivery Manifest — USD baseline + localized payment currency.
 *
 * Customer-facing only: supplier cost and net yield are never rendered. The rows
 * come from buildCustomerPriceBreakdown, which derives the item line from the
 * total so those internal figures cannot be reconstructed by subtraction.
 */
export default function DynamicCheckoutPriceCard({ summary, compact = false }) {
  if (!summary) return null;

  const breakdown = buildCustomerPriceBreakdown(summary);
  const localCurrency = breakdown.localCurrency;
  const localTotal = breakdown.localTotal;
  const showLocal = localCurrency !== 'USD' && localTotal != null;

  if (compact) {
    return (
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm">
        <div className="flex justify-between text-slate-600">
          <span>Item price (USD)</span>
          <span className="font-mono">{formatMoney(breakdown.itemPrice)}</span>
        </div>
        <div className="flex justify-between text-slate-600">
          <span>Duties &amp; VAT/GCT (included)</span>
          <span className="font-mono text-amber-700">{formatMoney(breakdown.duties)}</span>
        </div>
        <div className="flex justify-between font-semibold text-emerald-700 mt-1 pt-1 border-t border-slate-200">
          <span>Landed total (USD)</span>
          <span className="font-mono">{formatMoney(breakdown.total)}</span>
        </div>
        {showLocal ? (
          <div className="flex justify-between text-xs text-slate-500 mt-1">
            <span>Local settlement ({localCurrency})</span>
            <span className="font-mono">{formatLocal(localTotal, localCurrency)}</span>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="bg-[#0f1422] rounded-2xl p-6 border border-gray-800 text-white shadow-xl max-w-md">
      <h3 className="text-lg font-bold border-b border-gray-800 pb-3 mb-4 text-orange-500">
        Guaranteed Premium Delivery Manifest
      </h3>
      {summary.countryName ? (
        <p className="text-xs text-gray-400 mb-3">
          Destination: {summary.countryName} ({summary.destinationCountry || '—'})
        </p>
      ) : null}

      <div className="space-y-2 text-sm text-gray-300">
        <div className="flex justify-between">
          <span>Item Price (Retail, USD):</span>
          <span className="font-mono">{formatMoney(breakdown.itemPrice)}</span>
        </div>
        <div className="flex justify-between">
          <span>Shipping, Insurance &amp; Concierge Handling (USD):</span>
          <span className="font-mono">{formatMoney(breakdown.shipping)}</span>
        </div>
        <div className="flex justify-between text-yellow-500">
          <span>Regional Duties, Levies &amp; VAT/GCT (USD):</span>
          <span className="font-mono">{formatMoney(breakdown.duties)}</span>
        </div>
      </div>

      <div className="border-t border-gray-800 mt-4 pt-4 space-y-2">
        <div className="flex justify-between items-baseline">
          <span className="text-base font-semibold text-white">Total Landed (USD):</span>
          <span className="text-2xl font-black font-mono text-emerald-400">
            {formatMoney(breakdown.total)}
          </span>
        </div>
        {showLocal ? (
          <div className="flex justify-between items-baseline text-orange-300">
            <span className="text-sm font-medium">Local payment total ({localCurrency}):</span>
            <span className="text-xl font-bold font-mono">{formatLocal(localTotal, localCurrency)}</span>
          </div>
        ) : null}
        {showLocal && breakdown.exchangeRate ? (
          <p className="text-[10px] text-gray-500 text-right">
            FX peg used: 1 USD = {breakdown.exchangeRate.toFixed(4)} {localCurrency}
          </p>
        ) : null}
      </div>

      <p className="text-[11px] text-gray-500 mt-3 text-center italic">
        Cross-border cargo fees, broker clearances, and regional tax assessments are locked at checkout.
      </p>
    </div>
  );
}
